/**
 * ContextPanel — Right column panel matching Profil design pattern.
 * Used on every panel page for consistency.
 *
 * Props:
 *   title       — Bold header
 *   desc        — Description paragraph
 *   benefits    — Array of benefit strings (shown with orange checkmarks)
 *   action      — "CO TERAZ" text
 *   link        — { to, title, subtitle } for bottom link card (optional)
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';

export default function ContextPanel({ title, desc, benefits = [], action, link, icon: LinkIcon }) {
  return (
    <div className="hidden lg:block lg:col-span-4">
      <div className="sticky top-[100px] space-y-4">
        {/* Main context card */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="context-panel">
          <h3 className="text-[15px] font-bold text-zinc-900 mb-2">{title}</h3>
          <p className="text-sm text-zinc-500 mb-4">{desc}</p>

          {benefits.length > 0 && (
            <div className="space-y-2 mb-4">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[#F47A3B]" />
                  </div>
                  <span className="text-sm text-zinc-600">{b}</span>
                </div>
              ))}
            </div>
          )}

          {action && (
            <div className="p-3 bg-[#F47A3B]/5 border border-[#F47A3B]/10 rounded-xl">
              <p className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider mb-1">Co teraz</p>
              <p className="text-sm text-[#F47A3B] font-medium">{action}</p>
            </div>
          )}
        </div>

        {/* Bottom link card (optional) */}
        {link && (
          <Link to={link.to} data-testid="context-panel-link">
            <div className="bg-white border border-zinc-200 rounded-2xl p-4 hover:border-[#F47A3B]/30 transition-colors group shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-3">
                {LinkIcon && (
                  <div className="w-9 h-9 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                    <LinkIcon className="w-4 h-4 text-[#F47A3B]" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-zinc-900">{link.title}</p>
                  <p className="text-[11px] text-zinc-400">{link.subtitle}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-[#F47A3B] transition-colors" />
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
